import { useCallback, useEffect, useRef } from "react"

/**
 * Generic polling hook.
 * Replaces the identical pollIntervalRef + clearPoll + startPolling pattern
 * that was duplicated in AbstractScreening and ExtractionDefinition.
 *
 * Usage:
 *   const { startPolling, stopPolling } = usePoll(2000)
 *   startPolling((stop) => {
 *     const data = await fetchSomething()
 *     if (data.status === "completed") stop()
 *   })
 */
const usePoll = (intervalMs) => {
    const intervalRef = useRef(null)

    const stopPolling = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current)
            intervalRef.current = null
        }
    }, [])

    const startPolling = useCallback(
        (callback) => {
            stopPolling()
            callback(stopPolling)
            intervalRef.current = setInterval(() => callback(stopPolling), intervalMs)
        },
        [intervalMs, stopPolling]
    )

    // Auto-cleanup on unmount
    useEffect(() => stopPolling, [stopPolling])

    return { startPolling, stopPolling }
}

export { usePoll }
export default usePoll
